import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EmtrService} from './emtr.service';
import {featureFlags, RolePermissionGuard, SharedConstant, SharedService} from '@ems/shared';
import {emtrConstant} from './constants/emtrConstants';
import {Subscription} from 'rxjs';
import {FeatureToggleClientService} from '@schwab/schwab-feature-toggle-angular';
import {distinctUntilChanged, map} from 'rxjs/operators';

@Component({
  selector: 'tr-emtr',
  templateUrl: './emtr.component.html'
})
export class EmtrComponent implements OnInit, OnDestroy {
  envProp: any;
  rulesSubMenu: any;
  isModalOpened: any;
  clientInfo: any;
  sharedConstant = SharedConstant;
  businessUnit: any;
  getConstant = emtrConstant;
  public clientInfoSubscribe: Subscription;
  private isModalOpenedSubscription: Subscription;
  hasEmtrAppAcc = true;
  userPolicy: any;
  accessForRbsOnly = false;
  accessToDBSClient = false;
  accessToSPSClient = false;
  accessWithRuleAgentDBSClient = false;
  accessWithRuleAgentSPSClient = false;
  restrictionSimulatorFlagEnabled = false;
  private readonly restrictionSimulatorFlagOffDelayMs = 1500;
  private restrictionSimulatorFlagSubscription: Subscription;
  private restrictionSimulatorFlagOffTimer: ReturnType<typeof setTimeout>;

  constructor(private route: ActivatedRoute, private emtrService: EmtrService, public sharedService: SharedService,
              @Inject('entitlementConst') public entitlementConst, public router: Router,
              private readonly featureToggleClient: FeatureToggleClientService,
              private readonly rolePermissionGuard: RolePermissionGuard) {
    this.route.data.subscribe(data => {
      this.sharedService.clearErrorMessage();
      this.envProp = data.envProp;
      this.emtrService.setEnvPath(this.envProp);
    });

  }

  ngOnInit() {
    this.userPolicy = this.sharedService.getPolicyResponse('success');
    this.watchRestrictionSimulatorFlag();
    /* Navigate to respective page based on the selected client access */
    this.clientInfoSubscribe = this.sharedService.selectedClientInfo.subscribe(clientInfo => {
        this.clientInfo = clientInfo;
        this.sharedService.orgIdValue = clientInfo ? clientInfo.orgId : 0;
        this.sharedService.businessUnitVal = clientInfo ? clientInfo.businessUnit : '';
        this.sharedService.selectedAllInfo.next('');
        this.hasEmtrAppAcc = this.sharedService.checkEntitlements(this.entitlementConst.navUUID.emtrAccess.emtrAppAccess.uuid, '', '');
        this.businessUnit = clientInfo ? clientInfo.businessUnit : '';
        this.setNavigation();
    });
    this.setNavigation();
    this.isModalOpenedSubscription = this.sharedService.isModalOpened.subscribe(value => {
      this.isModalOpened = value;
    });
  }

  private watchRestrictionSimulatorFlag() {
    this.restrictionSimulatorFlagSubscription?.unsubscribe();
    clearTimeout(this.restrictionSimulatorFlagOffTimer);

    this.restrictionSimulatorFlagSubscription = this.featureToggleClient
      .monitorFlag(featureFlags.restrictionSimulator)
      .pipe(
        map((flagValue) => flagValue === true),
        distinctUntilChanged()
      )
      .subscribe((isEnabled) => {
        if (isEnabled) {
          clearTimeout(this.restrictionSimulatorFlagOffTimer);
          if (!this.restrictionSimulatorFlagEnabled) {
            this.restrictionSimulatorFlagEnabled = true;
            this.setNavigation();
          }
          return;
        }

        if (!this.restrictionSimulatorFlagEnabled) {
          return;
        }

        clearTimeout(this.restrictionSimulatorFlagOffTimer);
        this.restrictionSimulatorFlagOffTimer = setTimeout(
          () => this.disableRestrictionSimulatorIfConfirmed(),
          this.restrictionSimulatorFlagOffDelayMs
        );
      });
  }

  private disableRestrictionSimulatorIfConfirmed() {
    const unevaluated = 'unevaluated';
    const latestFlagValue = this.featureToggleClient.evaluateFlag(featureFlags.restrictionSimulator, unevaluated);

    if (latestFlagValue !== false) {
      return;
    }

    this.restrictionSimulatorFlagEnabled = false;
    this.setNavigation();

    if (this.router.url.startsWith('/ems/emtr/simulator')) {
      const fallbackRoute = this.rolePermissionGuard.getDefaultAccessibleRoute();
      this.router.navigateByUrl(fallbackRoute);
    }
  }

  setNavigation() {
    const getUUID = this.entitlementConst.navUUID;
    const isInternalUser = this.sharedService.isInternalApp();
    this.userPolicy = this.sharedService.getPolicyResponse('success');
              /* eslint-disable-next-line max-len */
              this.accessForRbsOnly = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.length === 1 && this.userPolicy.data.userPolicy.buPolicy.map(x => x.businessUnit).indexOf('RBS') !== -1) || (this.userPolicy.data.userPolicy.buPolicy === null && this.userPolicy.data.clients && this.userPolicy.data.clients.every(x => x.businessUnit === 'RBS'));
              /* eslint-disable-next-line max-len */
              this.accessToDBSClient = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'DBS')) || (this.userPolicy.data.userPolicy.buPolicy === null && this.userPolicy.data.clients && this.userPolicy.data.clients.some(x => x.businessUnit === 'DBS'));
              /* eslint-disable-next-line max-len */
              this.accessToSPSClient = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'SPS')) || (this.userPolicy.data.userPolicy.buPolicy === null && this.userPolicy.data.clients && this.userPolicy.data.clients.some(x => x.businessUnit === 'SPS'));
              /* eslint-disable-next-line max-len */
              this.accessWithRuleAgentDBSClient = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'DBS' && val.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1)) || (this.userPolicy.data.userPolicy.buPolicy === null && (this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients.some(y => x.clientId === y.clientId && y.businessUnit === 'DBS' && x.entitlementList.map(z => z.entitlementName).indexOf('RULES_SUMMARY') !== -1))));
              /* eslint-disable-next-line max-len */
              this.accessWithRuleAgentSPSClient = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'SPS' && val.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1)) || (this.userPolicy.data.userPolicy.buPolicy === null && (this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients.some(y => x.clientId === y.clientId && y.businessUnit === 'SPS' && x.entitlementList.map(z => z.entitlementName).indexOf('RULES_SUMMARY') !== -1))));
    this.rulesSubMenu = [
      {
        id: 1,
        name: 'Schwab Trade Rules<sup>&#174</sup>',
        route: 'rules',
        accessName: 'emtr',
        showLink: this.sharedService.checkEntitlements(getUUID.emtrAccess.ruleSummary.uuid, '', '')
      },
      {
        id: 2,
        /* eslint-disable-next-line max-len */
        name: this.businessUnit === 'RBS' || this.accessForRbsOnly || (this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentDBSClient && !this.accessWithRuleAgentSPSClient) || (!this.accessToDBSClient && !this.accessToSPSClient) || (this.accessToDBSClient && !this.accessToSPSClient && !this.accessWithRuleAgentDBSClient) || (!this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentSPSClient) ? this.sharedConstant['RBS'].empParticipantSubtabName : 'Manage Employees',
        /* eslint-disable-next-line max-len */
        route: this.businessUnit === 'RBS' || this.accessForRbsOnly || (this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentDBSClient && !this.accessWithRuleAgentSPSClient) || (!this.accessToDBSClient && !this.accessToSPSClient) || (this.accessToDBSClient && !this.accessToSPSClient && !this.accessWithRuleAgentDBSClient) || (!this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentSPSClient) ? this.sharedConstant['RBS'].empParticipantMainRoute : 'manage-employees',
        accessName: 'emtr',
        showLink: this.sharedService.checkEntitlements(getUUID.addLinkAppAccess.employeeSummary.uuid, '', '')
      },
      {
        id: 3,
        /* eslint-disable-next-line max-len */
        name: this.businessUnit === 'RBS' || this.accessForRbsOnly || (this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentDBSClient && !this.accessWithRuleAgentSPSClient) || (!this.accessToDBSClient && !this.accessToSPSClient) || (this.accessToDBSClient && !this.accessToSPSClient && !this.accessWithRuleAgentDBSClient) || (!this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentSPSClient) ? this.sharedConstant['RBS'].subTabName : 'Manage Employee Groups',
        /* eslint-disable-next-line max-len */
        route: this.businessUnit === 'RBS' || this.accessForRbsOnly || (this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentDBSClient && !this.accessWithRuleAgentSPSClient) || (!this.accessToDBSClient && !this.accessToSPSClient) || (this.accessToDBSClient && !this.accessToSPSClient && !this.accessWithRuleAgentDBSClient) || (!this.accessToDBSClient && this.accessToSPSClient && !this.accessWithRuleAgentSPSClient) ? this.sharedConstant['RBS'].mainRoute : 'manage-employee-groups',
        accessName: 'emtr',
        showLink: this.sharedService.checkEntitlements(getUUID.addLinkAppAccess.employeeSummary.uuid, '', '')
      },
      {
        id: 4,
        name: this.getConstant.restrictionSimulator.restrictionSimulatorText,
        route: 'simulator',
        accessName: 'emtr',
        hideIfNoAccess: true,
        showLink: isInternalUser && this.hasEmtrAppAcc && this.restrictionSimulatorFlagEnabled && this.sharedService.checkEntitlements(getUUID.emtrAccess.restrictionSimulatorSummary.uuid, '', '')
      },
      {
        id: 5,
        name: this.getConstant.instrumentGroups.instrumentGroupsText,
        route: 'instrument-groups',
        accessName: 'instrumentsGrp',
        showLink: this.sharedService.checkEntitlements(getUUID.emtrAccess.instrumentsSummary.uuid, '', '')
      },
      {
        id: 6,
        name: this.getConstant.manageMasterGroups.masterGroupText,
        route: 'master-groups',
        accessName: 'masterGrp',
        showLink: this.sharedService.checkEntitlements(getUUID.emtrAccess.masterAccountGroupSummary.uuid, '', '')
      },
      ];

    /* When client is selected and the selected client does not have access to INSTRUMENTS_SUMMARY entitlement */
    if (this.clientInfo && !this.sharedService.checkEntitlements(getUUID.emtrAccess.instrumentsSummary.uuid, '', '')) {
      if (!this.sharedService.checkEntitlements(getUUID.emtrAccess.ruleSummary.uuid, getUUID.accountSummary.uuid, '')) {
        this.sharedService.loadDataTrackValue('client_redirect');
        /* If selected client does not have Rule Agent and Auth Agent role access, then navigate to Admin menu */
        this.router.navigate(['ems/admin/manage-clients']);
      } else if (!this.hasEmtrAppAcc) {
        this.sharedService.loadDataTrackValue('client_redirect');
        /* If selected client does not have Rule Agent role access, then navigate to Workbench */
        this.router.navigate(['ems/workbench']);
      }
    } else if (this.clientInfo && !this.hasEmtrAppAcc) {
      this.sharedService.loadDataTrackValue('client_redirect');
      /* If selected client does not have RBS RA/RBS RA View role access but has Instrument Group Admin role, then navigate to Instrument Group ->
      That is when RBS client is selected with IGA role access */
      this.router.navigate(['/ems/emtr/instrument-groups/instrument-group-list']);
    }
  }

  ngOnDestroy() {
    if (this.clientInfoSubscribe) {
      this.clientInfoSubscribe.unsubscribe();
    }
    if (this.isModalOpenedSubscription) {
      this.isModalOpenedSubscription.unsubscribe();
    }
    if (this.restrictionSimulatorFlagSubscription) {
      this.restrictionSimulatorFlagSubscription.unsubscribe();
    }
    clearTimeout(this.restrictionSimulatorFlagOffTimer);
  }
}
